/**
 * Super Admin Dashboard - YUL ON TAP
 * 
 * Control Room Interface for Super Admin users.
 * - User management across all industries
 * - Account status control (active, suspended, locked)
 * - Password management (force reset, generate links)
 * - Audit log viewing
 * 
 * Visual: Enterprise, authoritative, powerful - not "friendly"
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { adminAPI, globalSurveyConfigAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import CommandBar from '../components/CommandBar';
import { toast } from 'sonner';
import {
  Shield,
  Users,
  Activity,
  Search,
  MoreVertical,
  Lock,
  Unlock,
  Key,
  Link2,
  UserX,
  UserCheck,
  RefreshCw,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Building2,
  UtensilsCrossed,
  Car,
  HeartPulse,
  ChevronLeft,
  ChevronRight,
  Filter,
  Plus,
  Trash2,
  ClipboardCheck,
  Edit,
  ArrowUp,
  ArrowDown,
  Save,
  Loader2,
} from 'lucide-react';

// Industry icons
const INDUSTRY_ICONS = {
  restaurant: UtensilsCrossed,
  automotive: Car,
  healthcare: HeartPulse,
  hotel: Building2,
};

// Status badge styles
const STATUS_CONFIG = {
  active: { 
    icon: CheckCircle2, 
    color: 'text-green-500', 
    bg: 'bg-green-500/10', 
    label: { en: 'Active', fr: 'Actif' } 
  },
  suspended: { 
    icon: AlertTriangle, 
    color: 'text-amber-500', 
    bg: 'bg-amber-500/10', 
    label: { en: 'Suspended', fr: 'Suspendu' } 
  },
  locked: { 
    icon: XCircle, 
    color: 'text-red-500', 
    bg: 'bg-red-500/10', 
    label: { en: 'Locked', fr: 'Verrouillé' } 
  },
};

// Role badge styles
const ROLE_CONFIG = {
  super_admin: { 
    color: 'text-purple-500', 
    bg: 'bg-purple-500/10', 
    label: { en: 'Super Admin', fr: 'Super Admin' } 
  },
  admin: { 
    color: 'text-blue-500', 
    bg: 'bg-blue-500/10', 
    label: { en: 'Admin', fr: 'Admin' } 
  },
  user: { 
    color: 'text-slate-500', 
    bg: 'bg-slate-500/10', 
    label: { en: 'User', fr: 'Utilisateur' } 
  },
};

export default function SuperAdminDashboard() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { checkTimeBasedPrompt } = useTheme();
  
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Default to 'hotel' industry for GlobalOnTap portal
  const [industryFilter, setIndustryFilter] = useState('hotel');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Add User Modal State
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    name: '',
    password: '',
    role: 'user',
    industry_type: 'hotel'
  });
  const [addingUser, setAddingUser] = useState(false);
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Survey Questions State
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [savingQuestion, setSavingQuestion] = useState(false);

  // Check if user is super_admin
  useEffect(() => {
    if (user?.role !== 'super_admin') {
      toast.error('Access denied. Super Admin only.');
      navigate('/login');
      return;
    }
    checkTimeBasedPrompt();
  }, [user, navigate, checkTimeBasedPrompt]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.listUsers({
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        industry: industryFilter !== 'all' ? industryFilter : undefined,
        page: currentPage,
        limit: 15,
      });
      setUsers(response.data.users || response.data);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, industryFilter, currentPage]);

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async () => {
    try {
      const response = await adminAPI.getAuditLogs({ limit: 50 });
      setAuditLogs(response.data.logs || response.data);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    }
  }, []);

  // Fetch survey questions
  const fetchSurveyQuestions = useCallback(async () => {
    setLoadingSurvey(true);
    try {
      const response = await globalSurveyConfigAPI.getConfig();
      setSurveyQuestions(response.data.questions || []);
    } catch (error) {
      console.error('Failed to load survey questions:', error);
    } finally {
      setLoadingSurvey(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchUsers();
      fetchAuditLogs();
    }
  }, [fetchUsers, fetchAuditLogs, user?.role]);

  // Survey question actions
  const handleUpdateQuestion = async (questionId, updates) => {
    setSavingQuestion(true);
    try {
      await globalSurveyConfigAPI.updateQuestion(questionId, updates);
      toast.success('Question updated');
      fetchSurveyQuestions();
      setEditingQuestion(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleReorderQuestion = async (questionId, direction) => {
    const currentIndex = surveyQuestions.findIndex(q => q.id === questionId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= surveyQuestions.length) return;
    
    const newOrder = [...surveyQuestions];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    
    try {
      await globalSurveyConfigAPI.reorderQuestions(newOrder.map(q => q.id));
      setSurveyQuestions(newOrder);
      toast.success('Question reordered');
    } catch (error) {
      toast.error('Failed to reorder questions');
    }
  };

  const handleToggleQuestion = async (questionId, enabled) => {
    try {
      await globalSurveyConfigAPI.updateQuestion(questionId, { enabled });
      toast.success(enabled ? 'Question enabled' : 'Question disabled');
      fetchSurveyQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    }
  };

  // User actions
  const handleUpdateStatus = async (userId, newStatus) => {
    setActionLoading(userId);
    try {
      await adminAPI.updateUserStatus(userId, newStatus);
      toast.success(`User ${newStatus === 'active' ? 'activated' : newStatus}`);
      fetchUsers();
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to update user status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleForceReset = async (userId) => {
    setActionLoading(userId);
    try {
      const response = await adminAPI.forcePasswordReset(userId);
      toast.success(`Temporary password: ${response.data.temporary_password}`);
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateResetLink = async (userId) => {
    setActionLoading(userId);
    try {
      const response = await adminAPI.generateResetLink(userId);
      navigator.clipboard.writeText(response.data.reset_link);
      toast.success('Reset link copied to clipboard');
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to generate reset link');
    } finally {
      setActionLoading(null);
    }
  };

  const handleInvalidateSessions = async (userId) => {
    setActionLoading(userId);
    try {
      await adminAPI.invalidateSessions(userId);
      toast.success('All sessions invalidated');
      fetchAuditLogs();
    } catch (error) {
      toast.error('Failed to invalidate sessions');
    } finally {
      setActionLoading(null);
    }
  };

  // Add User handler
  const handleAddUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error(i18n.language === 'fr' ? 'Veuillez remplir tous les champs' : 'Please fill all fields');
      return;
    }
    
    setAddingUser(true);
    try {
      await adminAPI.createUser(newUser);
      toast.success(i18n.language === 'fr' ? 'Utilisateur créé avec succès' : 'User created successfully');
      setShowAddUserModal(false);
      setNewUser({ email: '', name: '', password: '', role: 'user', industry_type: 'hotel' });
      fetchUsers();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || (i18n.language === 'fr' ? 'Échec de création' : 'Failed to create user'));
    } finally {
      setAddingUser(false);
    }
  };

  // Delete User handler
  const handleDeleteUser = async (userId) => {
    setActionLoading(userId);
    try {
      await adminAPI.deleteUser(userId);
      toast.success(i18n.language === 'fr' ? 'Utilisateur supprimé' : 'User deleted successfully');
      setDeleteConfirm(null);
      fetchUsers();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || (i18n.language === 'fr' ? 'Échec de suppression' : 'Failed to delete user'));
    } finally {
      setActionLoading(null);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Stats calculation
  const stats = {
    total: users.length,
    active: users.filter(u => u.account_status === 'active').length,
    suspended: users.filter(u => u.account_status === 'suspended').length,
    locked: users.filter(u => u.account_status === 'locked').length,
  };

  if (user?.role !== 'super_admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Command Bar */}
      <CommandBar
        title={i18n.language === 'fr' ? 'Centre de Contrôle' : 'Control Center'}
        subtitle={i18n.language === 'fr' ? 'Administration Système' : 'System Administration'}
        showWorkspaceIndicator={false}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {i18n.language === 'fr' ? 'Total Utilisateurs' : 'Total Users'}
                  </p>
                  <p className="text-2xl font-bold text-foreground mt-1">{stats.total}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {i18n.language === 'fr' ? 'Actifs' : 'Active'}
                  </p>
                  <p className="text-2xl font-bold text-green-500 mt-1">{stats.active}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {i18n.language === 'fr' ? 'Suspendus' : 'Suspended'}
                  </p>
                  <p className="text-2xl font-bold text-amber-500 mt-1">{stats.suspended}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {i18n.language === 'fr' ? 'Verrouillés' : 'Locked'}
                  </p>
                  <p className="text-2xl font-bold text-red-500 mt-1">{stats.locked}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex items-center justify-between">
            <TabsList className="bg-card border border-border">
              <TabsTrigger 
                value="users" 
                data-testid="tab-users"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Users className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Utilisateurs' : 'Users'}
              </TabsTrigger>
              <TabsTrigger 
                value="audit" 
                data-testid="tab-audit"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Activity className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Journal d\'Audit' : 'Audit Log'}
              </TabsTrigger>
              <TabsTrigger 
                value="platform" 
                data-testid="tab-platform"
                className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                onClick={() => navigate('/hotels/review-values')}
              >
                <Filter className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Valeurs Plateforme' : 'Platform Values'}
              </TabsTrigger>
              <TabsTrigger 
                value="survey" 
                data-testid="tab-survey"
                className="data-[state=active]:bg-green-600 data-[state=active]:text-white"
                onClick={() => fetchSurveyQuestions()}
              >
                <ClipboardCheck className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Questions Sondage' : 'Survey Questions'}
              </TabsTrigger>
            </TabsList>
            
            {/* Action buttons */}
            <div className="flex items-center space-x-2">
              <Button 
                size="sm" 
                onClick={() => setShowAddUserModal(true)}
                data-testid="add-user-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Ajouter' : 'Add User'}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { fetchUsers(); fetchAuditLogs(); }}
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Actualiser' : 'Refresh'}
              </Button>
            </div>
          </div>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={i18n.language === 'fr' ? 'Rechercher par email ou nom...' : 'Search by email or name...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-background"
                  data-testid="search-input"
                />
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full sm:w-auto">
                    <Filter className="w-4 h-4 mr-2" />
                    {i18n.language === 'fr' ? 'Statut' : 'Status'}: {statusFilter === 'all' ? (i18n.language === 'fr' ? 'Tous' : 'All') : statusFilter}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setStatusFilter('all')}>
                    {i18n.language === 'fr' ? 'Tous' : 'All'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatusFilter('active')}>
                    <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" />
                    {i18n.language === 'fr' ? 'Actif' : 'Active'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatusFilter('suspended')}>
                    <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
                    {i18n.language === 'fr' ? 'Suspendu' : 'Suspended'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setStatusFilter('locked')}>
                    <XCircle className="w-4 h-4 mr-2 text-red-500" />
                    {i18n.language === 'fr' ? 'Verrouillé' : 'Locked'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Industry filter - Hidden for GlobalOnTap (Hotels only) */}
              {/* Industry is locked to 'hotel' for this portal */}
              <div className="flex items-center px-3 py-2 rounded-md border border-input bg-secondary/50 text-sm text-muted-foreground">
                <Building2 className="w-4 h-4 mr-2 text-primary" />
                {i18n.language === 'fr' ? 'Hôtels seulement' : 'Hotels Only'}
              </div>
            </div>

            {/* Users Table */}
            <Card className="bg-card border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {i18n.language === 'fr' ? 'Utilisateur' : 'User'}
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {i18n.language === 'fr' ? 'Secteur' : 'Industry'}
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {i18n.language === 'fr' ? 'Rôle' : 'Role'}
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {i18n.language === 'fr' ? 'Statut' : 'Status'}
                      </th>
                      <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {i18n.language === 'fr' ? 'Dernière Connexion' : 'Last Login'}
                      </th>
                      <th className="text-right p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        {i18n.language === 'fr' ? 'Actions' : 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          <div className="flex items-center justify-center space-x-2">
                            <RefreshCw className="w-5 h-5 animate-spin" />
                            <span>{i18n.language === 'fr' ? 'Chargement...' : 'Loading...'}</span>
                          </div>
                        </td>
                      </tr>
                    ) : users.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          {i18n.language === 'fr' ? 'Aucun utilisateur trouvé' : 'No users found'}
                        </td>
                      </tr>
                    ) : (
                      users.map((u) => {
                        const IndustryIcon = INDUSTRY_ICONS[u.industry_type] || Building2;
                        const statusConfig = STATUS_CONFIG[u.account_status] || STATUS_CONFIG.active;
                        const roleConfig = ROLE_CONFIG[u.role] || ROLE_CONFIG.user;
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <tr 
                            key={u.id} 
                            className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                            data-testid={`user-row-${u.id}`}
                          >
                            <td className="p-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-sm font-semibold text-primary">
                                    {u.name?.[0]?.toUpperCase() || u.email?.[0]?.toUpperCase() || '?'}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-foreground">{u.name || '-'}</p>
                                  <p className="text-xs text-muted-foreground">{u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center space-x-2">
                                <IndustryIcon className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm text-foreground capitalize">{u.industry_type}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${roleConfig.bg} ${roleConfig.color}`}>
                                {roleConfig.label[i18n.language] || roleConfig.label.en}
                              </span>
                            </td>
                            <td className="p-4">
                              <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                                <StatusIcon className="w-3 h-3" />
                                <span>{statusConfig.label[i18n.language] || statusConfig.label.en}</span>
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                <span>{formatDate(u.last_login)}</span>
                              </div>
                            </td>
                            <td className="p-4 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    disabled={actionLoading === u.id}
                                    data-testid={`user-actions-${u.id}`}
                                  >
                                    {actionLoading === u.id ? (
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <MoreVertical className="w-4 h-4" />
                                    )}
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuItem 
                                    className="text-xs text-muted-foreground"
                                    disabled
                                  >
                                    {i18n.language === 'fr' ? 'Gestion du Compte' : 'Account Management'}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  
                                  {u.account_status === 'active' ? (
                                    <DropdownMenuItem 
                                      onClick={() => handleUpdateStatus(u.id, 'suspended')}
                                      className="text-amber-500"
                                    >
                                      <UserX className="w-4 h-4 mr-2" />
                                      {i18n.language === 'fr' ? 'Suspendre' : 'Suspend'}
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem 
                                      onClick={() => handleUpdateStatus(u.id, 'active')}
                                      className="text-green-500"
                                    >
                                      <UserCheck className="w-4 h-4 mr-2" />
                                      {i18n.language === 'fr' ? 'Réactiver' : 'Reactivate'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  {u.account_status !== 'locked' ? (
                                    <DropdownMenuItem 
                                      onClick={() => handleUpdateStatus(u.id, 'locked')}
                                      className="text-red-500"
                                    >
                                      <Lock className="w-4 h-4 mr-2" />
                                      {i18n.language === 'fr' ? 'Verrouiller' : 'Lock'}
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem 
                                      onClick={() => handleUpdateStatus(u.id, 'active')}
                                      className="text-green-500"
                                    >
                                      <Unlock className="w-4 h-4 mr-2" />
                                      {i18n.language === 'fr' ? 'Déverrouiller' : 'Unlock'}
                                    </DropdownMenuItem>
                                  )}
                                  
                                  <DropdownMenuSeparator />
                                  
                                  <DropdownMenuItem 
                                    className="text-xs text-muted-foreground"
                                    disabled
                                  >
                                    {i18n.language === 'fr' ? 'Sécurité' : 'Security'}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  
                                  <DropdownMenuItem onClick={() => handleForceReset(u.id)}>
                                    <Key className="w-4 h-4 mr-2" />
                                    {i18n.language === 'fr' ? 'Forcer Reset MDP' : 'Force Password Reset'}
                                  </DropdownMenuItem>
                                  
                                  <DropdownMenuItem onClick={() => handleGenerateResetLink(u.id)}>
                                    <Link2 className="w-4 h-4 mr-2" />
                                    {i18n.language === 'fr' ? 'Générer Lien Reset' : 'Generate Reset Link'}
                                  </DropdownMenuItem>
                                  
                                  <DropdownMenuItem 
                                    onClick={() => handleInvalidateSessions(u.id)}
                                    className="text-red-500"
                                  >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    {i18n.language === 'fr' ? 'Invalider Sessions' : 'Invalidate Sessions'}
                                  </DropdownMenuItem>
                                  
                                  <DropdownMenuSeparator />
                                  
                                  <DropdownMenuItem 
                                    onClick={() => setDeleteConfirm(u)}
                                    className="text-red-500"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    {i18n.language === 'fr' ? 'Supprimer' : 'Delete User'}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'fr' ? `Page ${currentPage} sur ${totalPages}` : `Page ${currentPage} of ${totalPages}`}
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="space-y-4">
            <Card className="bg-card border-border overflow-hidden">
              <CardHeader className="pb-3">
                <CardTitle className="text-foreground flex items-center space-x-2">
                  <Activity className="w-5 h-5" />
                  <span>{i18n.language === 'fr' ? 'Journal d\'Audit Système' : 'System Audit Log'}</span>
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  {i18n.language === 'fr' 
                    ? 'Toutes les actions administratives sont enregistrées ici'
                    : 'All administrative actions are recorded here'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="audit-table">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {i18n.language === 'fr' ? 'Horodatage' : 'Timestamp'}
                        </th>
                        <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {i18n.language === 'fr' ? 'Action' : 'Action'}
                        </th>
                        <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {i18n.language === 'fr' ? 'Utilisateur Cible' : 'Target User'}
                        </th>
                        <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {i18n.language === 'fr' ? 'Effectué Par' : 'Performed By'}
                        </th>
                        <th className="text-left p-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {i18n.language === 'fr' ? 'Détails' : 'Details'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="p-8 text-center text-muted-foreground">
                            {i18n.language === 'fr' ? 'Aucune entrée d\'audit' : 'No audit entries'}
                          </td>
                        </tr>
                      ) : (
                        auditLogs.map((log, idx) => (
                          <tr 
                            key={log.id || idx} 
                            className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                          >
                            <td className="p-4">
                              <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                <span>{formatDate(log.timestamp || log.created_at)}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary">
                                {log.action}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-foreground">
                              {log.target_email || log.user_id || '-'}
                            </td>
                            <td className="p-4 text-sm text-foreground">
                              {log.performed_by_email || log.performed_by || '-'}
                            </td>
                            <td className="p-4 text-sm text-muted-foreground max-w-xs truncate">
                              {typeof log.details === 'object' 
                                ? JSON.stringify(log.details) 
                                : log.details || '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Survey Questions Tab */}
          <TabsContent value="survey" className="space-y-4">
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <ClipboardCheck className="w-5 h-5 mr-2 text-green-500" />
                  {i18n.language === 'fr' ? 'Questions de Sondage Check-In' : 'Check-In Survey Questions'}
                </CardTitle>
                <CardDescription>
                  {i18n.language === 'fr' 
                    ? 'Gérez les questions par défaut pour les sondages de check-in des invités. Ces questions s\'appliquent à tous les nouveaux hôtels.'
                    : 'Manage the default questions for guest check-in surveys. These questions apply to all new hotels.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSurvey ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-green-500" />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {surveyQuestions.map((question, index) => (
                      <div 
                        key={question.id}
                        className={`p-4 rounded-lg border transition-colors ${
                          question.enabled 
                            ? 'border-border bg-card' 
                            : 'border-border/50 bg-muted/30 opacity-60'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-500/20 text-green-500 text-xs font-medium">
                                {index + 1}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                question.response_type === 'rating' 
                                  ? 'bg-blue-500/20 text-blue-400' 
                                  : 'bg-amber-500/20 text-amber-400'
                              }`}>
                                {question.response_type === 'rating' ? '⭐ Rating' : '👍 Yes/No/Neutral'}
                              </span>
                              {!question.enabled && (
                                <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">
                                  Disabled
                                </span>
                              )}
                            </div>
                            
                            {editingQuestion === question.id ? (
                              <div className="space-y-2">
                                <Input
                                  placeholder="English text"
                                  defaultValue={question.question_text_en}
                                  id={`edit-en-${question.id}`}
                                  className="text-sm"
                                />
                                <Input
                                  placeholder="French text"
                                  defaultValue={question.question_text_fr}
                                  id={`edit-fr-${question.id}`}
                                  className="text-sm"
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    disabled={savingQuestion}
                                    onClick={() => {
                                      const enInput = document.getElementById(`edit-en-${question.id}`);
                                      const frInput = document.getElementById(`edit-fr-${question.id}`);
                                      handleUpdateQuestion(question.id, {
                                        question_text_en: enInput.value,
                                        question_text_fr: frInput.value
                                      });
                                    }}
                                  >
                                    {savingQuestion ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingQuestion(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <p className="text-sm font-medium text-foreground">
                                  🇬🇧 {question.question_text_en}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  🇫🇷 {question.question_text_fr}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              disabled={index === 0}
                              onClick={() => handleReorderQuestion(question.id, 'up')}
                              title="Move up"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              disabled={index === surveyQuestions.length - 1}
                              onClick={() => handleReorderQuestion(question.id, 'down')}
                              title="Move down"
                            >
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setEditingQuestion(question.id)}
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={`h-8 w-8 ${question.enabled ? 'text-green-500' : 'text-muted-foreground'}`}
                              onClick={() => handleToggleQuestion(question.id, !question.enabled)}
                              title={question.enabled ? 'Disable' : 'Enable'}
                            >
                              {question.enabled ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {surveyQuestions.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No survey questions configured</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Add User Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {i18n.language === 'fr' ? 'Ajouter un utilisateur' : 'Add New User'}
            </DialogTitle>
            <DialogDescription>
              {i18n.language === 'fr' 
                ? 'Créer un nouveau compte utilisateur'
                : 'Create a new user account'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">{i18n.language === 'fr' ? 'Courriel' : 'Email'}</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{i18n.language === 'fr' ? 'Nom' : 'Name'}</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{i18n.language === 'fr' ? 'Mot de passe' : 'Password'}</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{i18n.language === 'fr' ? 'Rôle' : 'Role'}</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">{i18n.language === 'fr' ? 'Employé' : 'Employee'}</SelectItem>
                    <SelectItem value="location_manager">{i18n.language === 'fr' ? 'Gérant Location' : 'Location Manager'}</SelectItem>
                    <SelectItem value="client_admin">{i18n.language === 'fr' ? 'Admin Client' : 'Client Admin'}</SelectItem>
                    <SelectItem value="franchise_admin">{i18n.language === 'fr' ? 'Admin Franchise' : 'Franchise Admin'}</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{i18n.language === 'fr' ? 'Secteur' : 'Industry'}</Label>
                {/* GlobalOnTap portal: Hotel only - industry locked */}
                <div className="flex items-center h-10 px-3 rounded-md border border-input bg-secondary text-foreground">
                  <Building2 className="w-4 h-4 mr-2 text-primary" />
                  {i18n.language === 'fr' ? 'Hôtel' : 'Hotel'}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {i18n.language === 'fr' ? 'GlobalOnTap est un portail Hôtels uniquement' : 'GlobalOnTap is a Hotels-only portal'}
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserModal(false)}>
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button onClick={handleAddUser} disabled={addingUser}>
              {addingUser ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {i18n.language === 'fr' ? 'Création...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  {i18n.language === 'fr' ? 'Créer' : 'Create'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-500">
              {i18n.language === 'fr' ? 'Confirmer la suppression' : 'Confirm Delete'}
            </DialogTitle>
            <DialogDescription>
              {i18n.language === 'fr' 
                ? `Êtes-vous sûr de vouloir supprimer ${deleteConfirm?.name || deleteConfirm?.email}? Cette action est irréversible.`
                : `Are you sure you want to delete ${deleteConfirm?.name || deleteConfirm?.email}? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteUser(deleteConfirm?.id)}
              disabled={actionLoading === deleteConfirm?.id}
            >
              {actionLoading === deleteConfirm?.id ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {i18n.language === 'fr' ? 'Suppression...' : 'Deleting...'}
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {i18n.language === 'fr' ? 'Supprimer' : 'Delete'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
